import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Product, ProductCollection } from '../types';
import { getProductsByCollection, getCollections } from '../services/firebase';
import Header from '../components/organisms/Header';
import Footer from '../components/organisms/Footer';
import PromotionalBanner from '../components/molecules/PromotionalBanner';
import LogoSpinner from '../components/atoms/LogoSpinner';
import './CollectionDetailPage.css';
import { useStorefrontData } from '../hooks/useStorefrontData';

interface ProductCardProps {
  product: Product;
  collectionName: string;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, collectionName }) => {
  const navigate = useNavigate();
  const primaryImage = product.images.find(img => img.isPrimary)?.url || 
                      (product.images.length > 0 ? product.images[0].url : undefined);

  const handleViewDetails = () => {
    navigate(`/catalog/${collectionName}/${product.id}`);
  };

  return (
    <div className="collection-details-product-card">
      <div 
        className="collection-details-product-card-image-wrapper"
        onClick={handleViewDetails}
      >
        {primaryImage ? (
          <img 
            src={primaryImage} 
            alt={product.name} 
            className="collection-details-product-card-image"
          />
        ) : (
          <div className="collection-details-product-card-placeholder">
            <span>No Image</span>
          </div>
        )}
      </div>
      <div className="collection-details-product-card-content">
        <h3 
          className="collection-details-product-card-title"
          onClick={handleViewDetails}
        >
          {product.name}
        </h3>
        {/* <p className="collection-details-product-card-description">{product.description}</p> */}
        <div className="collection-details-product-card-details">
          <span className="collection-details-product-card-flavors">
            {product.flavors.filter(f => f.available).length} flavors available
          </span>
          <span className="collection-details-product-card-sizes">
            {product.sizes.length} sizes available
          </span>
        </div>
        <button 
          className="collection-details-product-card-button"
          onClick={handleViewDetails}
        >
          View Details
        </button>
      </div>
    </div>
  );
};

const CollectionDetailPage: React.FC = () => {
  const { collectionName } = useParams<{ collectionName: string }>();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [collection, setCollection] = useState<ProductCollection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getPromotionalBannerData } = useStorefrontData();
  const bannerData = getPromotionalBannerData();

  const navLinks = [
    { text: 'Home', href: '/' },
    { text: 'Catalog', href: '/catalog' },
    { text: 'Contact', href: '/contact' }
  ];

  useEffect(() => {
    const fetchData = async () => {
      if (!collectionName) return;

      try {
        setLoading(true);
        
        // Fetch all collections to find the matching one
        const collections = await getCollections();
        const matchingCollection = collections.find(
          col => col.name.toLowerCase().replace(/\s+/g, '-') === collectionName
        );
        
        if (!matchingCollection) {
          setError('Collection not found');
          return;
        }

        setCollection(matchingCollection);

        // Fetch products for this collection
        const collectionProducts = await getProductsByCollection(matchingCollection.id);
        setProducts(collectionProducts);
      } catch (err) {
        console.error('Error fetching collection data:', err);
        setError('Failed to load collection. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [collectionName]);

  const handleEmailSubmit = (email: string): void => {
    console.log('Email subscription:', email);
  };

  const handleFollowShop = (): void => {
    console.log('Follow on shop clicked');
  };

  const handleBannerClick = (): void => {
    console.log('Banner arrow clicked');
  };

  const handleBackToCatalog = () => {
    navigate('/catalog');
  };

  if (loading) {
    return (
      <div className="App">
        <Header navLinks={navLinks} />
        <div className="loading-container">
          <LogoSpinner size="large" text="Loading collection..." />
        </div>
      </div>
    );
  }

  if (error || !collection) {
    return (
      <div className="App">
        <Header navLinks={navLinks} />
        <div className="error-container">
          <div className="error-message">{error || 'Collection not found'}</div>
          <button onClick={handleBackToCatalog} className="back-button">
            Back to Catalog
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Header navLinks={navLinks} />
      
      {bannerData && (
        <PromotionalBanner 
          message={bannerData.message}
          code={bannerData.code}
          dates={bannerData.dates}
          note={bannerData.note}
          onClick={handleBannerClick}
        />
      )}
      
      <main className="collection-details-main">
        <div className="collection-details-hero">
          <button onClick={handleBackToCatalog} className="back-to-catalog">
            ← Back to Collections
          </button>
          <h1>{collection.name}</h1>
          <p>{collection.description}</p>
        </div>
        
        <div className="collection-details-content">
          <div className="collection-details-products-grid">
            {products.length > 0 ? (
              products.map((product) => (
                <ProductCard 
                  key={product.id} 
                  product={product} 
                  collectionName={collectionName || ''}
                />
              ))
            ) : (
              <div className="no-products">
                <p>No products available in this collection yet.</p>
              </div>
            )}
          </div>
        </div>
      </main>
      
      <Footer 
        onEmailSubmit={handleEmailSubmit}
        onFollowShop={handleFollowShop}
      />
    </div>
  );
};

export default CollectionDetailPage;
